import { useState } from "react";
import axios from "axios";
import { Instagram, Mail, Music2, Palette, ShoppingBag } from "lucide-react";
import KineticHeading from "@/components/KineticHeading";
import Reveal from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const contactImage = "/contact-portrait.webp";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const updateField = (event) => setForm({ ...form, [event.target.name]: event.target.value });
  const submitForm = async (event) => {
    event.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Your note has been sent to Morgan's inbox.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("The note could not be sent. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="px-5 pb-28 pt-36 md:px-8 md:pt-44" data-testid="contact-page">
      <section className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-[1fr_0.85fr]">
        <div>
          <Reveal><p className="text-xs uppercase tracking-[0.35em] text-cocoa/50" data-testid="contact-eyebrow">Write something lovely</p></Reveal>
          <KineticHeading testId="contact-kinetic-heading" lines={["Leave a note", "between", "the pages."]} className="mt-6 font-display text-6xl font-light leading-[0.88] tracking-[-0.04em] md:text-8xl" />
          <Reveal delay={0.3}>
            <div className="mt-14 max-w-xl space-y-5" data-testid="contact-links-list">
              <a href="https://www.amazon.com.au/Almost-Always-Morgan-Ella-ebook/dp/B0HFR8L98Y" target="_blank" rel="noreferrer" data-testid="contact-amazon-link" className="group flex items-center justify-between border-b border-cocoa/15 py-5 transition-colors duration-300 hover:border-cocoa">
                <span className="font-display text-4xl italic">Buy on Amazon</span><ShoppingBag size={22} className="transition-transform duration-300 group-hover:-translate-y-1" />
              </a>
              <a href="https://www.redbubble.com/people/morgi2001/shop" target="_blank" rel="noreferrer" data-testid="contact-something-fun-link" className="group flex items-center justify-between border-b border-cocoa/15 py-5 transition-colors duration-300 hover:border-cocoa">
                <span className="font-display text-4xl italic">Something Fun</span><Palette size={22} className="transition-transform duration-300 group-hover:-translate-y-1" />
              </a>
              <a href="mailto:morgan.suljic21@gmail.com" data-testid="contact-email-link" className="group flex items-center justify-between gap-5 border-b border-cocoa/15 py-5 transition-colors duration-300 hover:border-cocoa">
                <span className="break-all font-display text-3xl italic md:text-4xl">morgan.suljic21@gmail.com</span><Mail size={22} className="shrink-0 transition-transform duration-300 group-hover:-translate-y-1" />
              </a>
              <a href="https://www.instagram.com/morgan_reads01/" target="_blank" rel="noreferrer" data-testid="contact-instagram-link" className="group flex items-center justify-between border-b border-cocoa/15 py-5 transition-colors duration-300 hover:border-cocoa">
                <span className="font-display text-4xl italic">Instagram</span><Instagram size={22} className="transition-transform duration-300 group-hover:-translate-y-1" />
              </a>
              <a href="https://www.tiktok.com/@morgan.reads8" target="_blank" rel="noreferrer" data-testid="contact-tiktok-link" className="group flex items-center justify-between border-b border-cocoa/15 py-5 transition-colors duration-300 hover:border-cocoa">
                <span className="font-display text-4xl italic">TikTok</span><Music2 size={22} className="transition-transform duration-300 group-hover:-translate-y-1" />
              </a>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <div className="relative rounded-[3rem] bg-lavender/70 p-6 md:p-8" data-testid="contact-form-panel">
            <div className="arch-frame mb-8 aspect-[16/9] overflow-hidden">
              <img src={contactImage} alt="Portrait of Morgan" data-testid="contact-visual-image" className="h-full w-full object-cover object-center" />
            </div>
            <form onSubmit={submitForm} className="space-y-7" data-testid="contact-form">
              <div>
                <label htmlFor="name" className="text-xs uppercase tracking-[0.25em] text-cocoa/50" data-testid="contact-name-label">Name</label>
                <Input id="name" name="name" value={form.name} onChange={updateField} required data-testid="contact-name-input" className="editorial-input mt-2 h-12 px-0 text-lg" placeholder="Your name" />
              </div>
              <div>
                <label htmlFor="email" className="text-xs uppercase tracking-[0.25em] text-cocoa/50" data-testid="contact-email-label">Email</label>
                <Input id="email" name="email" type="email" value={form.email} onChange={updateField} required data-testid="contact-email-input" className="editorial-input mt-2 h-12 px-0 text-lg" placeholder="you@example.com" />
              </div>
              <div>
                <label htmlFor="message" className="text-xs uppercase tracking-[0.25em] text-cocoa/50" data-testid="contact-message-label">Message</label>
                <Textarea id="message" name="message" value={form.message} onChange={updateField} required minLength={10} data-testid="contact-message-input" className="editorial-input mt-2 min-h-32 resize-none px-0 text-lg" placeholder="Tell us what kind of story you are dreaming about..." />
              </div>
              <Button type="submit" disabled={sending} data-testid="contact-submit-button" className="h-13 w-full rounded-full bg-cocoa py-6 text-base text-white transition-transform duration-300 hover:scale-[1.02] hover:bg-cocoa">
                {sending ? "Sealing the envelope..." : "Send the note"}
              </Button>
            </form>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
